import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { Trash2, Send } from "react-feather";
import { usePost } from "../../api";
import { useEditSession, PendingEdit } from "../../components/workbench";
import { IBatchUpdateRequest, IBatchUpdateResponse } from "../../interfaces";

interface ReviewSubmitModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const typeBadge = (type: string) => {
  const map: Record<string, string> = {
    deal: "primary",
    coupon: "info",
    offer: "warning",
  };
  return <Badge bg={map[type] || "secondary"}>{type}</Badge>;
};

function getChangedFields(edit: PendingEdit) {
  const fields: { field: string; oldValue: any; newValue: any }[] = [];
  for (const key of Object.keys(edit.changes)) {
    const oldVal = edit.original[key];
    const newVal = edit.changes[key];
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      fields.push({ field: key, oldValue: oldVal, newValue: newVal });
    }
  }
  return fields;
}

export const ReviewSubmitModal: React.FC<ReviewSubmitModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const { getEditsArray, removeEdit, clearAll, editCount } = useEditSession();
  const [comment, setComment] = useState("QA review batch update");
  const [result, setResult] = useState<IBatchUpdateResponse | null>(null);
  const submitBatch = usePost<IBatchUpdateResponse, IBatchUpdateRequest>();

  const edits = getEditsArray();

  const handleSubmit = async () => {
    const updates: IBatchUpdateRequest["updates"] = {
      deals: [],
      coupons: [],
      offers: [],
    };
    for (const edit of edits) {
      const list =
        edit.type === "deal"
          ? updates.deals!
          : edit.type === "coupon"
            ? updates.coupons!
            : updates.offers!;
      list.push({ id: edit.id, data: edit.changes });
    }

    try {
      const response = await submitBatch.execute("kindred/batch-update", {
        comment,
        updates,
      });
      setResult(response ?? null);
      if (response && response.failureCount === 0) {
        clearAll();
        onSuccess();
      }
    } catch (err) {
      console.error("Batch update failed:", err);
    }
  };

  const handleClose = () => {
    setResult(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Review Changes{" "}
          <Badge bg="primary" className="ms-2">
            {editCount}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result && (
          <Alert
            variant={result.failureCount === 0 ? "success" : "warning"}
            className="mb-3"
          >
            {result.successCount} succeeded, {result.failureCount} failed
            {result.results
              .filter((r) => !r.success)
              .map((r) => (
                <div key={r.id} className="small text-danger">
                  {r.type} {r.id}: {r.error}
                </div>
              ))}
          </Alert>
        )}

        {!result && edits.length === 0 && (
          <p className="text-muted text-center py-3">No pending changes.</p>
        )}

        {!result &&
          edits.map((edit) => {
            const changedFields = getChangedFields(edit);
            return (
              <Card key={`${edit.type}-${edit.id}`} className="mb-2">
                <Card.Header className="d-flex align-items-center justify-content-between py-2">
                  <div>
                    {typeBadge(edit.type)}{" "}
                    <span className="fw-semibold ms-1">{edit.entityName}</span>
                    <span className="text-muted small ms-2">
                      {edit.id.substring(0, 8)}...
                    </span>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeEdit(edit.type, edit.id)}
                  >
                    <Trash2 size={12} />
                  </Button>
                </Card.Header>
                <Card.Body className="p-2">
                  {changedFields.length === 0 ? (
                    <span className="text-muted small">No changes</span>
                  ) : (
                    <Table size="sm" className="mb-0" borderless>
                      <thead>
                        <tr>
                          <th className="small">Field</th>
                          <th className="small">Old</th>
                          <th className="small">New</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changedFields.map((f) => (
                          <tr key={f.field}>
                            <td className="small fw-semibold">{f.field}</td>
                            <td className="small text-danger">
                              {JSON.stringify(f.oldValue) ?? "—"}
                            </td>
                            <td className="small text-success">
                              {JSON.stringify(f.newValue) ?? "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            );
          })}

        {!result && edits.length > 0 && (
          <div className="mt-3">
            <label className="form-label small">Comment</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!result && edits.length > 0 && (
          <>
            <Button variant="outline-danger" size="sm" onClick={clearAll}>
              Discard All
            </Button>
            <Button
              variant="success"
              onClick={handleSubmit}
              disabled={submitBatch.loading || !comment}
            >
              {submitBatch.loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <Send size={14} className="me-1" />
                  Submit All ({editCount})
                </>
              )}
            </Button>
          </>
        )}
        {result && result.failureCount === 0 && (
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
